import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import MarkdownArticle from "./MarkdownArticle";
import WaybackArchiveButton from "./WaybackArchiveButton";
import {
  ArticleDraftInput,
  BlogArticle,
  useCreateArticleMutation,
  useGetArticleForEditQuery,
  useGetBlogMetaQuery,
  useGetMyBooksQuery,
  usePublishArticleMutation,
  useUnpublishArticleMutation,
  useUpdateArticleMutation,
} from "../../services/rtkApi/blogApi";

/**
 * Write / edit a blog post. Reached at `/blog/new` and `/blog/manage/:id`,
 * both behind `RequireWriterRoute` (admin or `writer`).
 *
 * The body is plain markdown with a formatting toolbar; the preview renders
 * through the same `MarkdownArticle` the public article page uses, so the
 * draft and the published post cannot drift apart.
 */

type ToolbarAction = {
  label: string;
  title: string;
  /** wraps the selection, or inserts at the caret when nothing is selected */
  apply: (selected: string) => { text: string; caretOffset?: number };
};

const TOOLBAR: ToolbarAction[] = [
  { label: "H2", title: "heading", apply: (s) => ({ text: `## ${s || "heading"}` }) },
  { label: "B", title: "bold", apply: (s) => ({ text: `**${s || "bold"}**` }) },
  { label: "I", title: "italic", apply: (s) => ({ text: `_${s || "italic"}_` }) },
  { label: "S", title: "strikethrough", apply: (s) => ({ text: `~~${s || "struck"}~~` }) },
  { label: "link", title: "link", apply: (s) => ({ text: `[${s || "text"}](https://)` }) },
  { label: "img", title: "image", apply: (s) => ({ text: `![${s || "alt"}](https://)` }) },
  { label: "code", title: "inline code", apply: (s) => ({ text: `\`${s || "code"}\`` }) },
  { label: "```", title: "code block", apply: (s) => ({ text: `\`\`\`\n${s || "code"}\n\`\`\`` }) },
  { label: "quote", title: "blockquote", apply: (s) => ({ text: `> ${s || "quoted"}` }) },
  { label: "• list", title: "bullet list", apply: (s) => ({ text: `- ${s || "item"}` }) },
  { label: "1. list", title: "numbered list", apply: (s) => ({ text: `1. ${s || "item"}` }) },
  { label: "—", title: "divider", apply: () => ({ text: `\n---\n` }) },
];

/** how long to sit on "loading" before offering a retry instead (see the effect below) */
const LOAD_TIMEOUT_MS = 8_000;

const emptyDraft: ArticleDraftInput = {
  title: "",
  subtitle: "",
  content: "",
  category: "editorial",
  tags: [],
  coverImageUrl: "",
  bookId: null,
};

const BlogEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const { data: meta } = useGetBlogMetaQuery();
  const { data: booksData } = useGetMyBooksQuery();
  const {
    data: existing,
    isLoading: isLoadingArticle,
    error: loadError,
    refetch: refetchArticle,
  } = useGetArticleForEditQuery(id || "", { skip: isNew });

  const [createArticle, { isLoading: isCreating }] = useCreateArticleMutation();
  const [updateArticle, { isLoading: isUpdating }] = useUpdateArticleMutation();
  const [publishArticle, { isLoading: isPublishing }] = usePublishArticleMutation();
  const [unpublishArticle, { isLoading: isUnpublishing }] = useUnpublishArticleMutation();

  const [draft, setDraft] = useState<ArticleDraftInput>(emptyDraft);
  const [tagInput, setTagInput] = useState("");
  const [showPreview, setShowPreview] = useState(true);
  const [status, setStatus] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const [loadTimedOut, setLoadTimedOut] = useState(false);

  /**
   * Bounds the loading state. If the access token is not in memory yet when the
   * page mounts, the shared reauth wrapper refreshes and retries the request —
   * and that retry can settle without the query ever leaving `pending`, which
   * would otherwise leave a writer staring at a spinner forever. After
   * LOAD_TIMEOUT_MS with nothing to show, offer a retry instead.
   */
  useEffect(() => {
    if (isNew || existing || loadError) {
      setLoadTimedOut(false);
      return;
    }
    const timer = setTimeout(() => setLoadTimedOut(true), LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [isNew, existing, loadError]);

  // hydrate the form once the article arrives
  useEffect(() => {
    if (!existing) return;
    setDraft({
      title: existing.title,
      subtitle: existing.subtitle || "",
      content: existing.content || "",
      category: existing.category,
      tags: existing.tags || [],
      coverImageUrl: existing.coverImageUrl || "",
      bookId: existing.bookId ?? null,
      chapterNumber: existing.chapterNumber,
    });
  }, [existing]);

  const categories = meta?.categories ?? ["editorial"];
  const books = booksData?.books ?? [];
  const selectedBook = books.find((b) => b._id === draft.bookId);
  const busy = isCreating || isUpdating || isPublishing || isUnpublishing;
  const isPublished = existing?.status === "published";

  const set = <K extends keyof ArticleDraftInput>(key: K, value: ArticleDraftInput[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setStatus(null);
  };

  /** Wraps the current textarea selection with a toolbar action and restores focus. */
  const applyFormat = useCallback((action: ToolbarAction) => {
    const el = bodyRef.current;
    if (!el) return;
    const start = el.selectionStart ?? 0;
    const end = el.selectionEnd ?? 0;
    const value = el.value;
    const selected = value.slice(start, end);
    const { text } = action.apply(selected);

    const next = `${value.slice(0, start)}${text}${value.slice(end)}`;
    setDraft((d) => ({ ...d, content: next }));

    // put the caret after the inserted block on the next paint
    requestAnimationFrame(() => {
      el.focus();
      const caret = start + text.length;
      el.setSelectionRange(caret, caret);
    });
  }, []);

  const addTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (!tag) return;
    if ((draft.tags || []).includes(tag)) {
      setTagInput("");
      return;
    }
    set("tags", [...(draft.tags || []), tag].slice(0, 12));
    setTagInput("");
  };

  const removeTag = (tag: string) =>
    set("tags", (draft.tags || []).filter((t) => t !== tag));

  const validate = (): string | null => {
    if (!draft.title.trim()) return "give the post a title first";
    if (!draft.content.trim()) return "the post is empty";
    return null;
  };

  const persist = async (publish: boolean): Promise<BlogArticle | null> => {
    const problem = validate();
    if (problem) {
      setError(problem);
      return null;
    }
    setError(null);

    const payload: ArticleDraftInput = {
      title: draft.title.trim(),
      subtitle: draft.subtitle?.trim() || undefined,
      content: draft.content,
      category: draft.category,
      tags: draft.tags,
      coverImageUrl: draft.coverImageUrl?.trim() || undefined,
      // null detaches a chapter back into a standalone post
      bookId: draft.bookId || null,
      chapterNumber: draft.bookId ? draft.chapterNumber : undefined,
    };

    try {
      if (isNew) {
        const created = await createArticle({
          ...payload,
          status: publish ? "published" : "draft",
        }).unwrap();
        setStatus(publish ? "published" : "draft saved");
        navigate(`/blog/manage/${created._id}`, { replace: true });
        return created;
      }

      const saved = await updateArticle({ id: id!, patch: payload }).unwrap();
      if (publish && saved.status !== "published") {
        await publishArticle(id!).unwrap();
        setStatus("published");
      } else {
        setStatus("saved");
      }
      return saved;
    } catch (err: any) {
      setError(err?.data?.error || "could not save the post");
      return null;
    }
  };

  const handleUnpublish = async () => {
    if (!id) return;
    try {
      await unpublishArticle(id).unwrap();
      setStatus("moved back to draft");
    } catch (err: any) {
      setError(err?.data?.error || "could not unpublish");
    }
  };

  const wordCount = useMemo(
    () => (draft.content.trim() ? draft.content.trim().split(/\s+/).length : 0),
    [draft.content],
  );

  // a writer opening someone else's post by URL gets a 403 — say so rather than
  // leaving them on a spinner or handing them a blank form to overwrite with
  if (!isNew && (loadError || loadTimedOut)) {
    const status = (loadError as { status?: number } | undefined)?.status;
    return (
      <PageContainer>
        <Inner>
          <BackLink to="/blog/manage">&larr; all posts</BackLink>
          <Centered>
            {status === 403
              ? "this post belongs to someone else."
              : status === 404
                ? "that post does not exist."
                : "could not load that post."}
          </Centered>
          {status !== 403 && status !== 404 && (
            <Centered>
              <SecondaryButton
                type="button"
                onClick={() => {
                  setLoadTimedOut(false);
                  refetchArticle();
                }}
              >
                try again
              </SecondaryButton>
            </Centered>
          )}
        </Inner>
      </PageContainer>
    );
  }

  if (!isNew && (isLoadingArticle || !existing)) {
    return (
      <PageContainer>
        <Centered>loading post…</Centered>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Inner>
        <TopBar>
          <BackLink to="/blog/manage">&larr; all posts</BackLink>
          <TopBarRight>
            {existing && (
              <StatusPill $published={isPublished}>
                {isPublished ? "published" : "draft"}
              </StatusPill>
            )}
            {status && <SavedNote>{status}</SavedNote>}
          </TopBarRight>
        </TopBar>

        <Title>{isNew ? "write a post" : "edit post"}</Title>

        {error && <ErrorBanner role="alert">{error}</ErrorBanner>}

        <Field>
          <Label htmlFor="post-title">title</Label>
          <TextInput
            id="post-title"
            value={draft.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="what is this about?"
            maxLength={200}
          />
        </Field>

        <Field>
          <Label htmlFor="post-subtitle">subtitle</Label>
          <TextInput
            id="post-subtitle"
            value={draft.subtitle || ""}
            onChange={(e) => set("subtitle", e.target.value)}
            placeholder="one line under the title — also used as the excerpt"
            maxLength={300}
          />
        </Field>

        <TwoUp>
          <Field>
            <Label htmlFor="post-category">category</Label>
            <Select
              id="post-category"
              value={draft.category}
              onChange={(e) => set("category", e.target.value)}
            >
              {categories.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </Select>
          </Field>

          <Field>
            <Label htmlFor="post-cover">cover image url</Label>
            <TextInput
              id="post-cover"
              value={draft.coverImageUrl || ""}
              onChange={(e) => set("coverImageUrl", e.target.value)}
              placeholder="https://… (optional)"
            />
          </Field>
        </TwoUp>

        <TwoUp>
          <Field>
            <Label htmlFor="post-book">part of a book</Label>
            <Select
              id="post-book"
              value={draft.bookId || ""}
              onChange={(e) => set("bookId", e.target.value || null)}
            >
              <option value="">— standalone post —</option>
              {books.map((b) => (
                <option key={b._id} value={b._id}>
                  {b.title}
                  {b.status === "draft" ? " (draft book)" : ""}
                </option>
              ))}
            </Select>
            {books.length === 0 && (
              <FieldNote>
                no books yet — <Link to="/blog/books/new">start one</Link> to group posts
                into chapters.
              </FieldNote>
            )}
          </Field>

          <Field>
            <Label htmlFor="post-chapter">chapter number</Label>
            <TextInput
              id="post-chapter"
              type="number"
              min={1}
              disabled={!draft.bookId}
              value={draft.chapterNumber ?? ""}
              onChange={(e) =>
                set("chapterNumber", e.target.value ? Number(e.target.value) : undefined)
              }
              placeholder={draft.bookId ? "next in the book" : "pick a book first"}
            />
            {selectedBook && (
              <FieldNote>
                {selectedBook.chapterCount} chapter
                {selectedBook.chapterCount === 1 ? "" : "s"} so far · leave blank to append
                at the end
              </FieldNote>
            )}
          </Field>
        </TwoUp>

        <Field>
          <Label htmlFor="post-tags">tags</Label>
          <TagRow>
            {(draft.tags || []).map((tag) => (
              <TagChip key={tag}>
                {tag}
                <RemoveTag
                  type="button"
                  onClick={() => removeTag(tag)}
                  aria-label={`remove tag ${tag}`}
                >
                  ×
                </RemoveTag>
              </TagChip>
            ))}
            <TagInput
              id="post-tags"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") {
                  e.preventDefault();
                  addTag();
                }
              }}
              onBlur={addTag}
              placeholder={(draft.tags || []).length ? "add another…" : "coffee, aotd, …"}
            />
          </TagRow>
        </Field>

        <EditorHeader>
          <Label as="span">body — markdown</Label>
          <EditorHeaderRight>
            <WordCount>{wordCount} words</WordCount>
            <PreviewToggle
              type="button"
              onClick={() => setShowPreview((v) => !v)}
              $active={showPreview}
            >
              {showPreview ? "hide preview" : "show preview"}
            </PreviewToggle>
          </EditorHeaderRight>
        </EditorHeader>

        <Toolbar>
          {TOOLBAR.map((action) => (
            <ToolButton
              key={action.label}
              type="button"
              title={action.title}
              onClick={() => applyFormat(action)}
            >
              {action.label}
            </ToolButton>
          ))}
        </Toolbar>

        <Split $split={showPreview}>
          <BodyTextarea
            ref={bodyRef}
            value={draft.content}
            onChange={(e) => set("content", e.target.value)}
            placeholder={"# a heading\n\nwrite the post here. **bold**, _italic_, [links](https://n450s.com), lists, quotes and code blocks all work."}
            spellCheck
          />
          {showPreview && (
            <PreviewPane>
              <PreviewLabel>preview</PreviewLabel>
              <MarkdownArticle
                content={draft.content}
                placeholder="start typing to see the post take shape"
              />
            </PreviewPane>
          )}
        </Split>

        <Actions>
          <PrimaryButton type="button" onClick={() => persist(true)} disabled={busy}>
            {isPublished ? "save changes" : "publish"}
          </PrimaryButton>
          {!isPublished && (
            <SecondaryButton type="button" onClick={() => persist(false)} disabled={busy}>
              save draft
            </SecondaryButton>
          )}
          {isPublished && (
            <>
              <SecondaryButton type="button" onClick={handleUnpublish} disabled={busy}>
                unpublish
              </SecondaryButton>
              <ViewLink to={`/blog/${existing?.slug}`}>view post ↗</ViewLink>
            </>
          )}
        </Actions>

        {existing && (
          <ArchiveSection>
            <ArchiveTitle>archive</ArchiveTitle>
            <WaybackArchiveButton
              articleId={existing._id}
              initialWayback={existing.wayback}
              disabled={!isPublished}
              disabledReason={
                !isPublished ? "publish the post before archiving it" : undefined
              }
            />
          </ArchiveSection>
        )}
      </Inner>
    </PageContainer>
  );
};

const PageContainer = styled.div`
  min-height: 100vh;
  background-color: #f0f0f0;
  padding: 20px;

  @media (max-width: 700px) {
    padding: 12px;
  }
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Centered = styled.p`
  text-align: center;
  color: #888;
  padding: 60px 0;
`;

const TopBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const TopBarRight = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BackLink = styled(Link)`
  color: #466a5d;
  text-decoration: none;
  font-size: 0.9rem;

  &:hover {
    text-decoration: underline;
  }
`;

const ViewLink = styled(Link)`
  align-self: center;
  color: #466a5d;
  font-size: 0.88rem;
  text-decoration: underline;
`;

const StatusPill = styled.span<{ $published: boolean }>`
  font-size: 0.7rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 3px 9px;
  border: 1px solid ${(p) => (p.$published ? "#466a5d" : "#b58b2a")};
  background: ${(p) => (p.$published ? "#d4f9eb" : "#fdf1d3")};
  color: #333;
`;

const SavedNote = styled.span`
  font-size: 0.82rem;
  color: #466a5d;
`;

const Title = styled.h1`
  font-size: 2rem;
  color: #222;
  text-transform: uppercase;
  margin: 0 0 20px;
  text-shadow: 2px 2px 5px #3c3c3cae;
`;

const ErrorBanner = styled.div`
  background: #fde3e0;
  border: 1px solid #a3453b;
  color: #7a2e26;
  padding: 10px 14px;
  margin-bottom: 16px;
  font-size: 0.9rem;
`;

const Field = styled.div`
  margin-bottom: 14px;
`;

const FieldNote = styled.p`
  font-size: 0.76rem;
  color: #888;
  margin: 5px 0 0;
  line-height: 1.45;

  a {
    color: #466a5d;
  }
`;

const TwoUp = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 14px;

  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 0;
  }
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin-bottom: 5px;
`;

const TextInput = styled.input`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #ccc;
  background: white;
  font-size: 1rem;
  font-family: inherit;
  color: #222;

  &:focus {
    outline: none;
    border-color: #466a5d;
    box-shadow: 1px 2px rgba(0, 0, 0, 0.15);
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 9px 12px;
  border: 1px solid #ccc;
  background: white;
  font-size: 1rem;
  font-family: inherit;
  color: #222;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  padding: 6px;
  border: 1px solid #ccc;
  background: white;
  min-height: 42px;
`;

const TagChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.78rem;
  color: #444;
  background: #f5f5f5;
  border: 1px solid #ddd;
  padding: 3px 6px 3px 10px;
`;

const RemoveTag = styled.button`
  border: none;
  background: none;
  cursor: pointer;
  font-size: 1rem;
  line-height: 1;
  color: #888;
  padding: 0 2px;

  &:hover {
    color: #a3453b;
  }
`;

const TagInput = styled.input`
  flex: 1;
  min-width: 140px;
  border: none;
  outline: none;
  font-size: 0.9rem;
  font-family: inherit;
  padding: 4px;
`;

const EditorHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 12px;
  margin-top: 6px;
`;

const EditorHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 5px;
`;

const WordCount = styled.span`
  font-size: 0.78rem;
  color: #888;
`;

const PreviewToggle = styled.button<{ $active: boolean }>`
  font-size: 0.78rem;
  padding: 4px 10px;
  cursor: pointer;
  border: 1px solid ${(p) => (p.$active ? "#466a5d" : "#ccc")};
  background: ${(p) => (p.$active ? "#d4f9eb" : "white")};
  color: #444;
`;

const Toolbar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  padding: 6px;
  border: 1px solid #ccc;
  border-bottom: none;
  background: #fafafa;
`;

const ToolButton = styled.button`
  font-size: 0.78rem;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  padding: 4px 9px;
  cursor: pointer;
  border: 1px solid #ddd;
  background: white;
  color: #444;

  &:hover {
    background: #d4f9eb;
    border-color: #466a5d;
  }

  &:active {
    transform: translate(1px, 1px);
  }
`;

const Split = styled.div<{ $split: boolean }>`
  display: grid;
  grid-template-columns: ${(p) => (p.$split ? "1fr 1fr" : "1fr")};
  gap: 16px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const BodyTextarea = styled.textarea`
  width: 100%;
  min-height: 460px;
  padding: 16px;
  border: 1px solid #ccc;
  background: white;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 0.92rem;
  line-height: 1.6;
  color: #222;
  resize: vertical;

  &:focus {
    outline: none;
    border-color: #466a5d;
  }
`;

const PreviewPane = styled.div`
  position: sticky;
  top: 16px;
  max-height: 80vh;
  overflow-y: auto;

  @media (max-width: 900px) {
    position: static;
    max-height: none;
  }
`;

const PreviewLabel = styled.div`
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #888;
  margin-bottom: 6px;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
`;

const PrimaryButton = styled.button`
  padding: 10px 22px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  border: 1px solid #466a5d;
  background: #9beece;
  color: #222;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.5);

  &:hover:not(:disabled) {
    box-shadow: 3px 4px rgba(0, 0, 0, 0.5);
  }

  &:active:not(:disabled) {
    transform: translate(1px, 1px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled(PrimaryButton)`
  background: white;
  border-color: #555;
  box-shadow: 2px 3px rgba(0, 0, 0, 0.25);
`;

const ArchiveSection = styled.div`
  margin-top: 28px;
  padding-top: 18px;
  border-top: 1px solid #ddd;
  padding-bottom: 40px;
`;

const ArchiveTitle = styled.h2`
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #555;
  margin: 0 0 10px;
`;

export default BlogEditorPage;
